import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { RootState } from "../index";
import { ResponseMetaData, ResponseObjectList } from "../../entities/global";
import { getUsers, getUserCard, getUserTransactions, getUserCohortHistory } from "./thunks";
import { CohortHistoryItem, UserCard, UserListItem, WalletTransaction } from "../../entities/user";

interface UsersState {
  loading: boolean;
  loaded: boolean;
  users: UserListItem[];
  meta: ResponseMetaData;
  card?: UserCard;
  cardLoading: boolean;
  transactions: WalletTransaction[];
  transactionsMeta: ResponseMetaData;
  transactionsLoading: boolean;
  cohortHistory: CohortHistoryItem[];
  cohortHistoryLoading: boolean;
}

const initialState: UsersState = {
  loading: false,
  loaded: false,
  users: [],
  meta: { limit: 50, page: 1, pages: 1, total: 0 },
  cardLoading: false,
  transactions: [],
  transactionsMeta: { limit: 100, page: 1, pages: 1, total: 0 },
  transactionsLoading: false,
  cohortHistory: [],
  cohortHistoryLoading: false,
};

export const usersSlice = createSlice({
  name: "users",
  initialState,
  reducers: {
    onClearCard: (state) => {
      state.card = undefined;
      state.transactions = [];
      state.cohortHistory = [];
    },
  },
  extraReducers: (builder) => {
    builder.addCase(getUsers.pending, (state) => {
      state.loading = true;
      state.loaded = false;
    });
    builder.addCase(getUsers.fulfilled, (state, action: PayloadAction<ResponseObjectList<UserListItem[]>>) => {
      const { data, ...meta } = action.payload;
      state.users = data;
      state.meta = meta;
      state.loading = false;
      state.loaded = true;
    });
    builder.addCase(getUsers.rejected, (state) => {
      state.loading = false;
      state.loaded = true;
    });

    builder.addCase(getUserCard.pending, (state) => {
      state.cardLoading = true;
    });
    builder.addCase(getUserCard.fulfilled, (state, action: PayloadAction<UserCard | null>) => {
      state.card = action.payload ?? undefined;
      state.cardLoading = false;
    });
    builder.addCase(getUserCard.rejected, (state) => {
      state.cardLoading = false;
    });

    builder.addCase(getUserTransactions.pending, (state) => {
      state.transactionsLoading = true;
    });
    builder.addCase(
      getUserTransactions.fulfilled,
      (state, action: PayloadAction<ResponseObjectList<WalletTransaction[]>>) => {
        const { data, ...meta } = action.payload;
        state.transactions = data;
        state.transactionsMeta = meta;
        state.transactionsLoading = false;
      }
    );
    builder.addCase(getUserTransactions.rejected, (state) => {
      state.transactionsLoading = false;
    });

    builder.addCase(getUserCohortHistory.pending, (state) => {
      state.cohortHistoryLoading = true;
    });
    builder.addCase(getUserCohortHistory.fulfilled, (state, action: PayloadAction<CohortHistoryItem[]>) => {
      state.cohortHistory = action.payload;
      state.cohortHistoryLoading = false;
    });
    builder.addCase(getUserCohortHistory.rejected, (state) => {
      state.cohortHistoryLoading = false;
    });
  },
});

export const { onClearCard } = usersSlice.actions;
export const selectUsersState = (state: RootState) => state.users;
export default usersSlice.reducer;
